import { useEffect, useState } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import EmailField from "components/email-field";

import {
  resetUserPassword,
  selectAuthErrorMessage,
  selectAuthIsFetching,
  selectAuthIsSuccess
} from "../auth/auth-slice";
import ForgotPasswordConfirmation from "./forgot-password-confirmation.component";

interface IFormInput {
  email: string;
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFetching = useAppSelector(selectAuthIsFetching);
  const errorMessage = useAppSelector(selectAuthErrorMessage);
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<IFormInput>({
    mode: "onBlur",
    defaultValues: { email: "" }
  });

  const { isValid } = useFormState({ control });

  const onSubmit: SubmitHandler<IFormInput> = ({ email }) => {
    dispatch(resetUserPassword({ email }));
  };

  useEffect(() => {
    if (isSuccess) setOpen(true);
  }, [isSuccess]);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          {Boolean(errorMessage) && (
            <Alert icon={false} severity="error">
              {errorMessage}
            </Alert>
          )}
          <Box>
            <Typography variant="h3" gutterBottom component="div">
              Forgot your Password?
            </Typography>
            <Typography variant="subtitle1" fontSize={18} component="div">
              We’ll send you a link to create a new password.
            </Typography>
          </Box>
          <EmailField
            name="email"
            label="Email Address"
            control={control}
            errors={errors}
            required
          />
          <Box display="flex" justifyContent="space-space-between" gap={4}>
            <Button fullWidth variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              loading={isFetching}
              disabled={!isValid}
            >
              Submit
            </LoadingButton>
          </Box>
        </Stack>
      </Box>
      <ForgotPasswordConfirmation open={open} onClose={() => setOpen(false)} />
    </>
  );
}
